/**
 * Permisos — Mi Negocio → Permisos.
 *
 * Esta pantalla decía «por ahora esto solo se consulta» porque los permisos eran
 * un diccionario fijo en el código. Ronda D: ya se editan de verdad y el
 * servidor los obedece en los 36 módulos.
 *
 * POR QUÉ NO ES UNA CUADRÍCULA. Lo natural sería 36 filas × 10 columnas con una
 * casilla en cada cruce: 360 controles, imposibles de usar en un teléfono y
 * difíciles incluso en pantalla grande — hay que contar columnas con el dedo
 * para saber de quién es la que estás tocando. En su lugar se elige UN puesto y
 * se ven sus 36 módulos como una lista, cada uno con sus tres opciones. Se
 * responde una pregunta a la vez: «¿qué puede hacer el gerente?».
 *
 * Se guarda solo lo que cambió, y el botón dice cuántos cambios lleva pendientes
 * para que nadie se vaya creyendo que se guardó solo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Shield, Info, Eye, EyeOff, Pencil, RotateCcw, Loader2, Save, Lock } from 'lucide-react';
import { toast } from 'sonner';

const ICONO_DE_NIVEL = {
  ninguno: EyeOff,
  lectura: Eye,
  escritura: Pencil,
};

/** Las tres opciones de un módulo. Botones y no un desplegable: se ve de un
 *  golpe en qué está, sin abrir nada. */
function Opciones({ niveles, valor, onCambio, deshabilitado }) {
  return (
    <div className="inline-flex rounded-lg border border-[#E2E8F0] overflow-hidden" role="group">
      {niveles.map(n => {
        const Icono = ICONO_DE_NIVEL[n.clave] || Eye;
        const activo = valor === n.clave;
        return (
          <button
            key={n.clave}
            type="button"
            disabled={deshabilitado}
            onClick={() => onCambio(n.clave)}
            title={n.ayuda}
            aria-pressed={activo}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors
              ${activo
                ? 'bg-[#F72585] text-white'
                : 'bg-white text-[#64748B] hover:bg-[#F8F9FA]'}
              ${deshabilitado ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Icono className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{n.nombre}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function BusinessPermissions() {
  const [datos, setDatos] = useState(null);
  const [sujeto, setSujeto] = useState(null);
  const [pendientes, setPendientes] = useState({});   // 'sujeto|modulo' -> nivel
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(() => {
    setCargando(true);
    api.get('/tenant/permisos')
      .then(r => {
        setDatos(r.data);
        setPendientes({});
        // Se abre en el primer puesto editable: abrir en «Dueño», que no se
        // puede cambiar, haría pensar que la pantalla no funciona.
        const primero = (r.data.sujetos || []).find(s => s.editable);
        setSujeto(prev => prev || primero?.clave || null);
      })
      .catch(() => toast.error('No se pudieron cargar los permisos'))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actual = useMemo(
    () => (datos?.sujetos || []).find(s => s.clave === sujeto) || null,
    [datos, sujeto]);

  const nivelDe = (modulo) => {
    const clave = `${sujeto}|${modulo}`;
    if (clave in pendientes) return pendientes[clave];
    return datos?.matriz?.[sujeto]?.[modulo]?.nivel || 'ninguno';
  };

  const cambiar = (modulo, nivel) => {
    const clave = `${sujeto}|${modulo}`;
    const guardado = datos?.matriz?.[sujeto]?.[modulo]?.nivel;
    setPendientes(prev => {
      const copia = { ...prev };
      // Si vuelve a lo que ya estaba guardado, deja de ser un cambio pendiente.
      if (nivel === guardado) delete copia[clave];
      else copia[clave] = nivel;
      return copia;
    });
  };

  const cuantosPendientes = Object.keys(pendientes).length;

  const guardar = async () => {
    if (!cuantosPendientes) return;
    setGuardando(true);
    try {
      const cambios = Object.entries(pendientes).map(([clave, nivel]) => {
        const [suj, modulo] = clave.split('|');
        return { sujeto: suj, modulo, nivel };
      });
      const r = await api.put('/tenant/permisos', { cambios });
      toast.success(`Permisos guardados (${r.data?.guardados ?? cambios.length} cambios)`);
      cargar();
    } catch (e) {
      // El backend explica el motivo en castellano; se muestra tal cual en vez
      // de un «error al guardar» que no le sirve a nadie.
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar los permisos');
    } finally {
      setGuardando(false);
    }
  };

  const soloLectura = datos ? !datos.puedo_editar : true;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="business-permissions">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-5 h-5 text-[#F72585]" />
        <h3 className="text-lg font-semibold text-[#0F172A]">Quién puede entrar a qué</h3>
      </div>
      <p className="text-sm text-[#64748B] mb-4">
        Elige un puesto y decide, en cada parte del sistema, si esa persona no entra,
        solo consulta, o puede cambiar cosas.
      </p>

      <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 mb-6 flex gap-2.5">
        <Info className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#64748B] leading-relaxed space-y-1">
          <p>
            <strong className="text-[#0F172A]">Los cambios se aplican de verdad.</strong> En cuanto
            guardas, quien tenga ese puesto deja de ver el menú y de poder entrar por URL a lo que le quitaste.
            Si intenta entrar, el sistema se lo dice y le sugiere pedírtelo a ti.
          </p>
          <p>
            <strong className="text-[#0F172A]">El dueño siempre tiene acceso a todo</strong> y no se
            puede limitar. Es a propósito: si se pudiera, un clic te dejaría fuera de tu propio
            sistema y nadie podría devolverte la entrada.
          </p>
        </div>
      </div>

      {cargando && (
        <div className="flex items-center gap-2 text-sm text-[#64748B] py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando permisos…
        </div>
      )}

      {soloLectura && !cargando && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex gap-2.5">
          <Lock className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Puedes ver cómo están repartidos los permisos, pero cambiarlos es cosa del dueño o de
            un administrador del negocio.
          </p>
        </div>
      )}

      {datos && !cargando && (
        <>
          {/* Selector de puesto. Los tipos del giro (Mesero, Asesor de servicio)
              aparecen junto a los 5 puestos base, con su equivalencia visible. */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="permisos-sujetos">
            {(datos.sujetos || []).map(s => (
              <button
                key={s.clave}
                type="button"
                onClick={() => setSujeto(s.clave)}
                title={s.nota || undefined}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors
                  ${sujeto === s.clave
                    ? 'bg-[#F72585] text-white border-[#F72585]'
                    : 'bg-white text-[#0F172A] border-[#E2E8F0] hover:bg-[#F8F9FA]'}`}
              >
                {s.nombre}
                {s.tipo === 'tipo' && (
                  <span className={`ml-1.5 text-[11px] ${sujeto === s.clave ? 'text-white/80' : 'text-[#64748B]'}`}>
                    · como {s.nombre_rol_base}
                  </span>
                )}
                {!s.editable && (
                  <Lock className={`inline w-3 h-3 ml-1.5 ${sujeto === s.clave ? 'text-white/80' : 'text-[#64748B]'}`} />
                )}
              </button>
            ))}
          </div>

          {actual && !actual.editable && (
            <p className="text-sm text-[#64748B] bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 mb-5">
              {actual.nota || 'Este puesto no se puede limitar.'}
            </p>
          )}

          {actual?.nota && actual.editable && (
            <p className="text-sm text-[#64748B] mb-5">{actual.nota}</p>
          )}

          <div className="space-y-6" data-testid="permisos-modulos">
            {(datos.grupos || []).map(grupo => (
              <div key={grupo.grupo}>
                <h4 className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">
                  {grupo.grupo}
                </h4>
                <div className="space-y-2">
                  {grupo.modulos.map(m => {
                    const info = datos.matriz?.[sujeto]?.[m.clave];
                    const valor = nivelDe(m.clave);
                    const cambiado = `${sujeto}|${m.clave}` in pendientes;
                    const distintoDelSugerido = valor !== info?.por_defecto;
                    return (
                      <div
                        key={m.clave}
                        className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-lg border
                          ${cambiado ? 'border-[#F72585] bg-[#F8F9FA]' : 'border-[#E2E8F0] bg-white'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[#0F172A]">{m.etiqueta}</span>
                            {cambiado && (
                              <span className="text-[11px] font-semibold text-[#F72585]">sin guardar</span>
                            )}
                            {!cambiado && distintoDelSugerido && (
                              <button
                                type="button"
                                onClick={() => cambiar(m.clave, info.por_defecto)}
                                disabled={soloLectura || !actual?.editable}
                                className="inline-flex items-center gap-1 text-[11px] text-[#64748B] hover:text-[#0F172A]"
                                title="Volver al valor que trae el sistema para este puesto"
                              >
                                <RotateCcw className="w-3 h-3" /> volver al sugerido
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-[#64748B] mt-0.5">{m.ayuda}</p>
                        </div>
                        <Opciones
                          niveles={datos.niveles || []}
                          valor={valor}
                          onCambio={(n) => cambiar(m.clave, n)}
                          deshabilitado={soloLectura || !actual?.editable}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Barra de guardado pegada abajo: con 36 módulos el botón se queda
              muy lejos si vive al final de la página. */}
          {cuantosPendientes > 0 && (
            <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-4 bg-white border-t border-[#E2E8F0] flex items-center justify-between gap-3">
              <span className="text-sm text-[#64748B]">
                {cuantosPendientes} {cuantosPendientes === 1 ? 'cambio sin guardar' : 'cambios sin guardar'}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPendientes({})}
                  disabled={guardando}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-[#64748B] hover:bg-[#F8F9FA]"
                >
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={guardar}
                  disabled={guardando}
                  data-testid="permisos-guardar"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#F72585] hover:opacity-90 disabled:opacity-60"
                >
                  {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar cambios
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
